// Shared pagination footer for list pages (Quotations, Invoices,
// Receipts). Renders "Showing X to Y of Z" on the left + prev /
// page-pill / next + "N per page" selector on the right.
//
// Self-contained: callers pass total + current page + setters and
// the component handles bounds, disabled states, and the slice
// math display. Use a parent useEffect to clamp currentPage when
// the filtered total shrinks past it.
import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { cn } from '../../utils/cn';

export interface PaginationProps {
  total: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (p: number) => void;
  onPageSizeChange: (s: number) => void;
  pageSizeOptions?: number[];
  /** Singular noun for the count label, e.g. "invoices". */
  label?: string;
  className?: string;
}

export default function Pagination({
  total,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  label = 'items',
  className,
}: PaginationProps) {
  if (total <= 0) return null;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, total);

  const btn = (disabled: boolean) => cn(
    'inline-flex items-center justify-center h-8 w-8 rounded-lg border transition-colors',
    disabled
      ? 'border-gray-100 text-gray-300 cursor-not-allowed bg-white'
      : 'border-gray-200 text-gray-600 bg-white hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900',
  );

  return (
    <div className={cn(
      'px-5 py-3 border-t border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm',
      className,
    )}>
      <div className="text-gray-500 dark:text-gray-400 font-medium tracking-tight">
        Showing <span className="text-gray-900 dark:text-gray-100 font-semibold">{startIndex + 1}</span>
        {' '}to <span className="text-gray-900 dark:text-gray-100 font-semibold">{endIndex}</span>
        {' '}of <span className="text-gray-900 dark:text-gray-100 font-semibold">{total}</span> {label}
      </div>

      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className={btn(currentPage === 1)}
          aria-label="Previous page"
        >
          <ChevronLeftIcon className="w-4 h-4" />
        </button>

        <span className="inline-flex items-center justify-center h-8 min-w-8 px-2.5 rounded-lg bg-primary-600 text-white font-semibold text-sm shadow-sm">
          {currentPage}
        </span>

        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage >= totalPages}
          className={btn(currentPage >= totalPages)}
          aria-label="Next page"
        >
          <ChevronRightIcon className="w-4 h-4" />
        </button>

        <div className="relative ml-1">
          <select
            value={pageSize}
            onChange={(e) => { onPageSizeChange(Number(e.target.value)); onPageChange(1); }}
            className="h-8 pl-3 pr-8 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm font-medium appearance-none cursor-pointer hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
          >
            {pageSizeOptions.map((s) => (
              <option key={s} value={s}>{s} per page</option>
            ))}
          </select>
          <ChevronDownIcon className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      </div>
    </div>
  );
}
